import type { ReactNode } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import { useMe } from "../auth.tsx";
import { useApi } from "../api.ts";
import { fmtExpiry, fmtRelative, daysLeft } from "../format.ts";
import { Badge, Button, Card, Icon, LoadError, Loading, cn } from "../ui/index.tsx";
import { LogoMark } from "../ui/logo.tsx";
import { LEVEL_TONE, LoaderCard, SectionTitle, GameArt } from "./_shared.tsx";
import { Aurora, CountUp, EASE, Ring, Scramble, Springy, Stage, Tilt, rise, slideIn, stagger, useCalm, useGameMap } from "./member/kit.tsx";
import type { Announcement, Entitlement, Game } from "../../shared/types.ts";

function greeting(): string {
  const h = new Date().getHours();
  return h < 5 ? "Burning the midnight oil" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function HeroStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <motion.div variants={rise} className="rounded-lg border border-line bg-bg/50 px-4 py-3 backdrop-blur">
      <div className="kicker">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{children}</div>
    </motion.div>
  );
}

function Hero({ games }: { games: Game[] | undefined }) {
  const me = useMe();
  const calm = useCalm();
  const active = me.entitlements.filter((e) => e.active);
  const lifetime = active.filter((e) => e.expires_at === null).length;
  const timed = active.filter((e) => e.expires_at !== null).map((e) => daysLeft(e.expires_at!));
  const next = timed.length ? Math.max(0, Math.min(...timed)) : null;
  const listed = (games ?? []).filter((g) => g.active !== false);
  const up = listed.filter((g) => g.status === "operational").length;

  return (
    <motion.section className="pane hud hud-accent relative overflow-hidden p-6 sm:p-8"
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
      <Aurora />
      <div className="grid-floor pointer-events-none absolute inset-x-0 top-0 h-full opacity-40" aria-hidden />
      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
        <motion.div className="shrink-0" initial={{ scale: 0.4, rotate: -25, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.1 }}>
          <motion.div animate={calm ? undefined : { y: [0, -6, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
            <div className="relative">
              <div className="absolute inset-0 -z-10 scale-125 rounded-full bg-accent/30 blur-2xl" aria-hidden />
              <LogoMark className="size-20 drop-shadow-[0_8px_24px_rgb(255_61_79/0.45)] sm:size-28" />
            </div>
          </motion.div>
        </motion.div>

        <div className="min-w-0 flex-1">
          <div className="kicker flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-2 text-ok">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-ok/60 motion-reduce:hidden" />
                <span className="relative inline-flex size-2 rounded-full bg-ok" />
              </span>
              SYS.ONLINE
            </span>
            <span>{games ? `${up}/${listed.length} systems operational` : "linking systems"}</span>
          </div>
          <motion.p className="mt-3 text-sm text-muted" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>{greeting()},</motion.p>
          <h1 className="mt-0.5 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            <Scramble text={me.username} className="text-gradient" />
          </h1>
          <motion.div className="mt-5 flex flex-wrap gap-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}>
            <Link to="/downloads"><Springy><Button variant="primary" icon="download">Get the loader</Button></Springy></Link>
            <Link to="/licenses"><Springy><Button icon="key">Redeem a key</Button></Springy></Link>
          </motion.div>
        </div>
      </div>

      <motion.div className="relative mt-7 grid grid-cols-1 gap-3 min-[480px]:grid-cols-3" variants={stagger(0.09, 0.45)} initial="hidden" animate="show">
        <HeroStat label="Active games"><CountUp value={active.length} /></HeroStat>
        <HeroStat label="Lifetime"><CountUp value={lifetime} /></HeroStat>
        <HeroStat label="Next expiry">{next === null ? <span className="text-muted">None</span> : <><CountUp value={next} /><span className="ml-1 text-sm font-normal text-muted">{next === 1 ? "day" : "days"}</span></>}</HeroStat>
      </motion.div>
    </motion.section>
  );
}

function EntitlementCard({ e, game }: { e: Entitlement; game: Game | undefined }) {
  const lifetime = e.expires_at === null;
  const left = lifetime ? null : daysLeft(e.expires_at!);
  const expired = !e.active;
  const soon = !expired && left !== null && left <= 7;
  const color = expired ? "var(--color-bad)" : lifetime ? "var(--color-accent)" : soon ? "var(--color-warn)" : "var(--color-ok)";
  const fraction = expired ? 0 : lifetime ? 1 : Math.min(1, Math.max(0.04, (left ?? 0) / 30));

  return (
    <Tilt variants={rise} className={cn("pane hud overflow-hidden", lifetime && !expired && "hud-accent", expired && "opacity-75")}>
      <div className="relative">
        <GameArt game={{ name: e.game_name, image_url: game?.image_url ?? "", category: game?.category ?? "other" }} className="h-28 w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" />
        <div className="absolute left-3 top-3"><Badge tone={expired ? "bad" : "ok"} dot>{expired ? "Expired" : "Active"}</Badge></div>
      </div>
      <div className="relative -mt-9 flex items-end justify-between gap-3 px-4 pb-4">
        <div className="min-w-0 pb-1">
          <div className="truncate font-display text-lg font-semibold">{e.game_name}</div>
          <div className={cn("text-xs", soon ? "text-warn" : expired ? "text-bad" : "text-muted")}>{fmtExpiry(e.expires_at)}</div>
        </div>
        <Ring fraction={fraction} size={68} color={color} glow={lifetime && !expired} delay={0.2}>
          {lifetime && !expired ? (
            <span className="font-display text-2xl leading-none text-accent">∞</span>
          ) : expired ? (
            <Icon name="clock" className="size-5 text-bad" />
          ) : (
            <span className="text-center leading-none">
              <CountUp value={Math.max(0, left ?? 0)} className="font-display text-lg font-semibold tabular-nums" />
              <span className="block font-mono text-[9px] uppercase tracking-wider text-faint">days</span>
            </span>
          )}
        </Ring>
      </div>
    </Tilt>
  );
}

function EmptyGames() {
  const calm = useCalm();
  return (
    <Card hud className="relative overflow-hidden">
      <Aurora />
      <div className="relative flex flex-col items-center px-4 py-10 text-center">
        <div className="relative grid size-24 place-items-center">
          {!calm && [0, 1, 2].map((i) => (
            <motion.span key={i} className="absolute inset-0 rounded-full border border-accent/50" initial={{ scale: 0.5, opacity: 0.7 }}
              animate={{ scale: 1.9, opacity: 0 }} transition={{ duration: 3, delay: i, repeat: Infinity, ease: "easeOut" }} />
          ))}
          <motion.div className="grid size-16 place-items-center rounded-full border border-accent/40 bg-accent-soft text-accent"
            animate={calm ? undefined : { rotate: [-8, 8, -8] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
            <Icon name="key" className="size-7" />
          </motion.div>
        </div>
        <h3 className="mt-6 font-display text-xl font-semibold">No games unlocked yet</h3>
        <p className="mt-1 max-w-sm text-sm text-muted">Redeem a license key to light this panel up, or browse what's available.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Link to="/licenses"><Springy><Button variant="primary" icon="key">Redeem a key</Button></Springy></Link>
          <Link to="/pricing"><Springy><Button icon="card">See pricing</Button></Springy></Link>
        </div>
      </div>
    </Card>
  );
}

function Announcements() {
  const ann = useApi<Announcement[]>("/announcements");
  if (ann.loading && !ann.data) return <Loading />;
  if (ann.error && !ann.data) return <LoadError error={ann.error} retry={ann.reload} />;
  const items = ann.data ?? [];
  if (items.length === 0) return <p className="text-sm text-muted">Nothing new right now.</p>;
  const rail = { info: "bg-info", warning: "bg-warn", critical: "bg-bad" } as const;
  return (
    <motion.ul className="space-y-3" variants={stagger(0.1)} initial="hidden" animate="show">
      {items.slice(0, 5).map((a) => (
        <motion.li key={a.id} variants={slideIn} whileHover={{ x: 4 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="pane relative overflow-hidden p-4 pl-5">
          <span className={cn("absolute inset-y-0 left-0 w-1", rail[a.level] ?? "bg-info", a.level === "critical" && "animate-pulse-soft")} />
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Badge tone={LEVEL_TONE[a.level] ?? "info"}>{a.level}</Badge>
              <span className="truncate text-sm font-medium">{a.title}</span>
            </div>
            <span className="shrink-0 text-xs text-faint">{fmtRelative(a.created_at)}</span>
          </div>
          <p className="mt-2 whitespace-pre-line text-sm text-muted">{a.message}</p>
        </motion.li>
      ))}
    </motion.ul>
  );
}

export default function Home() {
  const me = useMe();
  const games = useApi<Game[]>("/games");
  const gameMap = useGameMap();
  const sorted = [...me.entitlements].sort((a, b) => Number(b.active) - Number(a.active));

  return (
    <Stage>
      <div className="space-y-8">
        <Hero games={games.data} />

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.25, ease: EASE }}>
          <LoaderCard />
        </motion.div>

        <section>
          <SectionTitle action={<Link to="/licenses" className="text-xs text-muted transition-colors hover:text-fg">Manage licenses</Link>}>Your games</SectionTitle>
          {sorted.length === 0 ? <EmptyGames /> : (
            <motion.div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" variants={stagger(0.08, 0.1)} initial="hidden" animate="show">
              {sorted.map((e) => <EntitlementCard key={e.game_id} e={e} game={gameMap.get(e.game_id)} />)}
            </motion.div>
          )}
        </section>

        <section>
          <SectionTitle>Announcements</SectionTitle>
          <Announcements />
        </section>
      </div>
    </Stage>
  );
}
